import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Film, Music, Trophy, Lightbulb, Play } from 'lucide-react';

export const AboutPage = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-foreground text-background py-20 md:py-32">
        <div className="container-custom text-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">
            Where Culture Meets Ownership
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-8 max-w-4xl mx-auto leading-tight">
            The Trade is a next-generation media platform covering the pulse of Hollywood, sports and culture
          </h1>
          <p className="text-background/70 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            From the creators and executives shaping the industry to the business moves redefining it.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-24 border-b border-border">
        <div className="container-custom max-w-4xl">
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-6">
            We deliver sharp analysis, exclusive stories and original perspectives that major outlets often overlook.
          </p>
          <p className="text-lg md:text-xl text-foreground leading-relaxed font-medium">
            Our coverage pulls back the curtain on how deals are made, how influence moves and how creators build ownership in an ever-changing media landscape.
          </p>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-4">
              Our Mission
            </h2>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-relaxed">
              To inform, inspire and empower creators and audiences by uncovering the strategy, ownership and storytelling that drive culture forward.
            </p>
          </div>
        </div>
      </section>

      {/* What We Cover */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-12 text-center">
            What We Cover
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="group p-8 bg-background border border-border hover:border-primary/50 transition-colors">
              <Film className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3">Film & Television</h3>
              <p className="text-muted-foreground leading-relaxed">
                The visionaries, movements and moments shifting the industry.
              </p>
            </div>
            
            <div className="group p-8 bg-background border border-border hover:border-primary/50 transition-colors">
              <Music className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3">Music & Culture</h3>
              <p className="text-muted-foreground leading-relaxed">
                Where sound, identity, and influence collide.
              </p>
            </div>
            
            <div className="group p-8 bg-background border border-border hover:border-primary/50 transition-colors">
              <Trophy className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3">Sports & Business</h3>
              <p className="text-muted-foreground leading-relaxed">
                How athletes are redefining ownership, brand power and media.
              </p>
            </div>
            
            <div className="group p-8 bg-background border border-border hover:border-primary/50 transition-colors">
              <Lightbulb className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3">Creative Economy</h3>
              <p className="text-muted-foreground leading-relaxed">
                IP rights, equity, innovation and long-game strategy.
              </p>
            </div>
            
            <div className="group p-8 bg-background border border-border hover:border-primary/50 transition-colors md:col-span-2 lg:col-span-2">
              <Play className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3">Original Series</h3>
              <p className="text-muted-foreground leading-relaxed">
                <span className="text-foreground font-medium">The Long Game</span>, <span className="text-foreground font-medium">Grind to Glory</span>, <span className="text-foreground font-medium">Blueprint</span>, <span className="text-foreground font-medium">Masked Filmmaker</span>, and more — exclusive storytelling that connects art with ambition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Voice */}
      <section className="py-16 md:py-24 bg-foreground text-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-8">
              Our Voice
            </h2>
            <p className="font-serif text-3xl md:text-4xl font-bold mb-12">
              Sharp. Honest. Disruptive.
            </p>
            <div className="space-y-6 text-lg text-background/80">
              <p>We celebrate excellence and challenge convention.</p>
              <p>We ask the questions others won't — and reveal the stories others can't.</p>
              <p className="text-background font-medium pt-4 border-t border-background/20 mt-8">
                Every piece we publish carries one purpose:<br />
                <span className="text-primary">To redefine what ownership, creativity, and culture truly mean.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Join The Movement */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">
              Join The Movement
            </h2>
            <p className="font-serif text-3xl md:text-4xl font-bold mb-6">
              This isn't just media.
            </p>
            <p className="font-serif text-2xl md:text-3xl text-muted-foreground mb-8">
              It's mentorship. It's mastery.
            </p>
            <p className="text-lg text-muted-foreground mb-10 leading-relaxed">
              Subscribe to The Trade Media newsletter or follow us on Instagram{' '}
              <a 
                href="https://www.instagram.com/thetrade.media" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                @thetrade.media
              </a>
              , and become part of the new era — where culture, commerce, and creativity trade places.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/submit">
                <Button size="lg" className="w-full sm:w-auto" data-testid="about-submit-btn">
                  Submit Your Story
                </Button>
              </Link>
              <a 
                href="https://www.instagram.com/thetrade.media" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="about-follow-btn">
                  Follow @thetrade.media
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 border-t border-border">
        <div className="container-custom">
          <div className="max-w-xl mx-auto text-center">
            <p className="text-muted-foreground">
              <strong className="text-foreground">Editorial & Partnerships:</strong>{' '}
              <a href="mailto:info@thetrade.media" className="text-primary hover:underline">
                info@thetrade.media
              </a>
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
